import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import {
  CreateOpoContactRequestBody,
  CreateOpoOrganizationRequestBody,
  UpdateOpoContactRequestBody,
  PatchOpoOrganizationRequestBody,
  CreateOpoUserRequestBody,
  UpdateOpoUserRequestBody,
  PatchOwnOpoContactRequestBody,
} from '@/api/apis/opo';
import { queryClient } from '@/queries';
import { opoQueryKeys, opoContactQueryKeys } from '@/queries/opo/opo.keys';
import { waitForOpoOperationResolve } from '@/queries/opo/utils';
import { userQueries } from '@/queries/user';

type OpoOrganizationInvalidationSource = {
  parentId?: string | null;
  organizationId?: string | null;
  opoOrganizationId?: string | null;
  memberships?: Array<{
    opoOrganizationId?: string | null;
  }>;
};

const getOpoOrganizationIdsForInvalidation = <T extends object>(variables: T) => {
  const { parentId, organizationId, opoOrganizationId, memberships } = variables as T &
    OpoOrganizationInvalidationSource;

  return Array.from(
    new Set(
      [
        parentId,
        organizationId,
        opoOrganizationId,
        ...(memberships?.map((membership) => membership.opoOrganizationId) ?? []),
      ].filter((id): id is string => Boolean(id)),
    ),
  );
};

const invalidateOpoOrganizationItems = async <T extends object>(variables: T) => {
  const organizationIds = getOpoOrganizationIdsForInvalidation(variables);

  await Promise.all(
    organizationIds.map((organizationId) =>
      queryClient.invalidateQueries({
        queryKey: opoQueryKeys.getItem(organizationId),
      }),
    ),
  );
};

export const useCreateOpoMutation = () => {
  return useMutation({
    mutationFn: (variables: CreateOpoOrganizationRequestBody) => api.opo.createOrganization(variables),
    meta: {
      invalidates: [opoQueryKeys.getList()],
    },
  });
};

export const useUpdateOpoMutation = () => {
  return useMutation({
    mutationFn: (variables: PatchOpoOrganizationRequestBody & { id: string }) => api.opo.updateOrganization(variables),
    meta: {
      invalidates: [opoQueryKeys.getList()],
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: opoQueryKeys.getItem(variables.id) });
    },
  });
};

export const useDeleteOpoMutation = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.opo.deleteOrganization(id),
    meta: {
      invalidates: [opoQueryKeys.getList()],
    },
  });
};

export const useCreateOpoContactMutation = () => {
  return useMutation({
    mutationFn: (variables: CreateOpoContactRequestBody) => api.opo.createContact(variables),
    meta: {
      invalidates: [opoContactQueryKeys.getList()],
    },
    onSuccess: async (_, variables) => {
      await invalidateOpoOrganizationItems(variables);
    },
  });
};

export const useCreateOpoUserMutation = () => {
  return useMutation({
    mutationFn: async (variables: CreateOpoUserRequestBody) => {
      const job = await api.opo.createUser(variables);

      return waitForOpoOperationResolve(job);
    },
    meta: {
      invalidates: [opoContactQueryKeys.getList()],
    },
    onSuccess: async (_, variables) => {
      await invalidateOpoOrganizationItems(variables);
    },
  });
};

export const useUpdateOpoContactMutation = () => {
  return useMutation({
    mutationFn: (variables: UpdateOpoContactRequestBody & { id: string }) => api.opo.updateContact(variables),
    meta: {
      invalidates: [opoContactQueryKeys.getList()],
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: opoContactQueryKeys.getItem(variables.id) });
    },
  });
};

export const useUpdateOpoUserMutation = () => {
  return useMutation({
    mutationFn: async (variables: UpdateOpoUserRequestBody & { id: string }) => {
      const job = await api.opo.updateUser(variables);

      return waitForOpoOperationResolve(job);
    },
    meta: {
      invalidates: [opoContactQueryKeys.getList()],
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: opoContactQueryKeys.getItem(variables.id) });
    },
  });
};

export const useUpdateMyOpoContactMutation = () => {
  return useMutation({
    mutationFn: (payload: PatchOwnOpoContactRequestBody) => api.opo.patchOwnContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueries.profile().queryKey });
    },
  });
};

export const useDeleteOpoContactMutation = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.opo.deleteContact(id),
    meta: {
      invalidates: [opoContactQueryKeys.getList()],
    },
  });
};
